import { PUBLIC_LANDING_RULES, type PublicLandingRule } from "./public-rules.js";

export type { PublicLandingRule } from "./public-rules.js";

const STEAM_LOGIN_PATH = "/api/identity/steam/openid/login";
const DISCORD_LOGIN_PATH = "/api/identity/discord/oauth/login";
const SERVER_LISTING_NAME = "The Nest - High AI - Storage - Global Chat";

interface PublicLandingContent {
  discordInviteUrl: string | null;
  rules: PublicLandingRule[];
}

export interface PublicLandingOptions {
  authNotice?: string;
  rules?: PublicLandingRule[];
  discordInviteUrl?: string | null;
}

export function publicLandingAuthNotice(searchParams: URLSearchParams): string | undefined {
  const steam = searchParams.get("steam");
  if (steam === "failed") return "Steam could not verify that sign-in. Nothing was changed; you can retry or use the Discord-first route.";
  if (steam === "conflict") return "That Steam account is already linked to another profile. Open a support ticket so staff can review it safely.";
  if (steam === "relink") return "A different Steam account is already linked to that profile. Staff must review account relinks.";

  const discord = searchParams.get("discord");
  if (["failed", "state_failed", "session_failed", "unavailable"].includes(discord ?? "")) {
    return "Discord could not complete that sign-in. Nothing was changed; please retry when you are ready.";
  }
  if (discord === "conflict") return "That Discord account already belongs to another player profile. Staff can review it through a support ticket.";
  if (discord === "relink") return "A different Discord account is already connected to that profile. Staff must review account relinks.";
  return undefined;
}

export function renderPublicLanding(options: PublicLandingOptions = {}): string {
  const rules = options.rules ?? PUBLIC_LANDING_RULES;
  const discordInviteUrl = safeDiscordInvite(options.discordInviteUrl);
  const authNotice = options.authNotice
    ? `<div class="landing-auth-notice" role="alert"><span aria-hidden="true"></span><p>${escapeLandingText(options.authNotice)}</p></div>`
    : "";

  return `
    <a class="landing-skip-link" href="#landing-content">Skip to content</a>
    <div class="public-landing" data-public-landing>
      <header class="landing-header" data-landing-header>
        <a class="landing-wordmark" href="#top" aria-label="The Nest home">
          <span class="landing-wordmark-mark"><img src="/portal/assets/the-nest-logo.png" alt="" /></span>
          <span class="landing-wordmark-copy"><strong>The Nest</strong><small>Gateway // Evrima</small></span>
        </a>
        <nav class="landing-desktop-nav" aria-label="Public site navigation">
          <a href="#about">About</a>
          <a href="#field-guide">Field guide</a>
          <a href="#rules">Rules</a>
        </nav>
        <a class="landing-header-access" href="#access"><span>Player portal</span><i aria-hidden="true">↗</i></a>
        <details class="landing-mobile-nav">
          <summary aria-label="Open site navigation"><span></span><span></span></summary>
          <nav aria-label="Mobile site navigation">
            <a href="#about">About</a>
            <a href="#field-guide">Field guide</a>
            <a href="#rules">Rules</a>
            <a href="#access">Player portal</a>
          </nav>
        </details>
      </header>

      <nav class="landing-chapter-rail" aria-label="Journey chapters" data-chapter-rail>
        <a href="#top" data-chapter-link="signal"><span>00</span><i></i><small>Signal</small></a>
        <a href="#about" data-chapter-link="breach"><span>01</span><i></i><small>Breach</small></a>
        <a href="#systems" data-chapter-link="record"><span>02</span><i></i><small>Record</small></a>
        <a href="#field-guide" data-chapter-link="arrival"><span>03</span><i></i><small>Arrival</small></a>
        <a href="#rules" data-chapter-link="protocols"><span>04</span><i></i><small>Protocols</small></a>
        <a href="#access" data-chapter-link="identity"><span>05</span><i></i><small>Identity</small></a>
      </nav>

      <aside class="landing-signal-dock" data-signal-dock aria-label="The Nest server signal" aria-hidden="true" inert>
        <span><i aria-hidden="true"></i>TN // SIGNAL LOCKED</span>
        <button type="button" data-copy-server data-server-name="${escapeLandingText(SERVER_LISTING_NAME)}">
          <strong>${escapeLandingText(SERVER_LISTING_NAME)}</strong><small data-copy-label>Copy server name</small>
        </button>
      </aside>

      <button class="landing-effects-toggle" type="button" data-motion-toggle aria-pressed="false"><i aria-hidden="true"></i><span>Effects on</span></button>

      <main id="landing-content">
        <section class="landing-hero" id="top" data-chapter="signal" aria-labelledby="landing-title">
          <div class="landing-hero-art" aria-hidden="true">
            <div class="landing-hero-image"></div>
            <div class="landing-glass"></div>
            <div class="landing-fog landing-fog-near"></div>
            <div class="landing-fog landing-fog-far"></div>
            <div class="landing-scan-beam"></div>
          </div>
          <div class="landing-hero-frame" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
          <div class="landing-hero-content">
            ${authNotice}
            <div class="landing-hero-grid">
              <div class="landing-hero-copy">
                <div class="landing-kicker"><span aria-hidden="true"></span>Gateway // Independent Evrima community</div>
                <h1 id="landing-title"><span>A sanctuary was never part of the experiment.</span><em>We built one anyway.</em></h1>
                <p>The Nest is a player-focused <strong>The Isle: Evrima</strong> server where survival has weight, dinosaurs have history, and the people running it are accountable.</p>
                <div class="landing-hero-actions">
                  <a class="landing-button landing-button-primary" href="#access"><span>Access player portal</span><i aria-hidden="true">↓</i></a>
                  <a class="landing-button landing-button-quiet" href="#about"><span>Begin the descent</span><i aria-hidden="true">↓</i></a>
                </div>
              </div>

              <aside class="landing-signal-card" aria-label="How to find The Nest in Evrima">
                <div class="landing-signal-card-head"><span>TN / FIELD SIGNAL 01</span><i>RECEIVING</i></div>
                <div class="landing-signal-graph" aria-hidden="true"><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b></div>
                <div class="landing-signal-card-body">
                  <small>Find us in the Evrima server list</small>
                  <strong>${escapeLandingText(SERVER_LISTING_NAME)}</strong>
                </div>
                <button class="landing-copy-signal" type="button" data-copy-server data-server-name="${escapeLandingText(SERVER_LISTING_NAME)}">
                  <span data-copy-label>Copy server name</span><i aria-hidden="true">⧉</i>
                </button>
              </aside>
            </div>
            <div class="landing-hero-foot">
              <span><i aria-hidden="true"></i>Portal access available</span>
              <span>Scroll to enter the facility</span>
            </div>
          </div>
        </section>

        <section class="landing-cinematic-chapter landing-breach" id="about" data-cinematic-chapter data-chapter="breach" aria-labelledby="about-title">
          <div class="landing-cinematic-stage">
            <div class="landing-cinematic-media landing-breach-media" aria-hidden="true">
              <div class="landing-cinematic-image"></div>
              <div class="landing-depth-glass"></div>
              <div class="landing-depth-fog"></div>
              <div class="landing-probe-light"></div>
            </div>
            <div class="landing-breach-layout">
              <div class="landing-section-index"><span>01</span><small>The breach</small></div>
              <div class="landing-breach-copy">
                <span class="landing-micro-label">APOLLO FIELD ARCHIVE // RECOVERED SIGNAL</span>
                <p class="landing-cinematic-prelude">The island is indifferent.</p>
                <h2 id="about-title"><span>The facility failed.</span><em>The community did not.</em></h2>
                <p>The Nest is an independent Evrima community built around fair survival, accountable staff, and dinosaurs whose history follows them beyond a single session.</p>
              </div>
              <aside class="landing-breach-dossier">
                <span>TN // OPERATING PRINCIPLE</span>
                <blockquote>“Build a server the community can genuinely call home.”</blockquote>
                <div><i aria-hidden="true"></i><small>Community habitat established</small></div>
              </aside>
            </div>
            <div class="landing-cinematic-cue"><span>Move through the observation wing</span><i></i></div>
          </div>
        </section>

        <section class="landing-cinematic-chapter landing-record" id="systems" data-cinematic-chapter data-chapter="record" aria-labelledby="systems-title">
          <div class="landing-cinematic-stage">
            <div class="landing-cinematic-media landing-record-media" aria-hidden="true">
              <div class="landing-cinematic-image"></div>
              <div class="landing-record-scanline"></div>
              <div class="landing-record-reticle"><span></span><span></span><span></span></div>
            </div>
            <div class="landing-record-layout">
              <header class="landing-record-heading">
                <span class="landing-micro-label">FIELD SYSTEMS // SPECIMEN LINK ACTIVE</span>
                <h2 id="systems-title">Survival leaves a record.</h2>
                <p>The portal supports the experience without replacing the game. Every system begins with what actually happened on Gateway.</p>
              </header>
              <div class="landing-record-console">
                <div class="landing-record-console-head"><span>TN / SPECIMEN TRACE</span><i>LIVE</i></div>
                <div class="landing-record-pulse" aria-hidden="true"><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b><b></b></div>
                <div class="landing-record-console-foot">
                  <span>IDENTITY VERIFIED</span>
                  <button class="landing-skunk-trigger" type="button" data-skunk-trigger aria-expanded="false" aria-controls="skunk-terminal" aria-label="Inspect anomalous terminal signal"><i></i><i></i><i></i></button>
                  <span>ARCHIVE WRITABLE</span>
                </div>
                <aside class="landing-skunk-terminal" id="skunk-terminal" data-skunk-terminal hidden aria-label="Unauthorised morale officer record">
                  <button type="button" data-skunk-close aria-label="Close hidden terminal">&times;</button>
                  <img src="/portal/assets/skunk-terminal-easter-egg.png" alt="Skunk, a moderator of The Nest" loading="lazy" decoding="async" />
                  <div>
                    <small>ANOMALOUS PERSONNEL FILE // SK-01</small>
                    <strong>Unauthorised morale officer detected.</strong>
                    <p>Biggest supporter on record. Facility morale restored.</p>
                  </div>
                </aside>
              </div>
              <div class="landing-record-ledger">
                <article data-record-beat="0"><span>01</span><div><small>Observation</small><h3>Live field terminal</h3><p>Current dinosaur, vitals, growth, Prime progress, garage actions, and recent ledger activity in one secure view.</p></div><i>OBS</i></article>
                <article data-record-beat="1"><span>02</span><div><small>Persistence</small><h3>Individual specimen storage</h3><p>Park and retrieve dinosaurs with their growth, mutations, Prime state, appearance, and history intact.</p></div><i>ARC</i></article>
                <article data-record-beat="2"><span>03</span><div><small>Exchange</small><h3>A living economy</h3><p>Earn Sticks through verified play, then use the Store, Marketplace, Eggs, and ledger-backed games.</p></div><i>EXC</i></article>
                <article data-record-beat="3"><span>04</span><div><small>Accountability</small><h3>Support with a paper trail</h3><p>Private cases, clear notifications, and audited staff outcomes stay attached to the correct player.</p></div><i>LOG</i></article>
              </div>
            </div>
            <div class="landing-record-counter" aria-hidden="true"><span data-record-counter>01</span><small>/ 04</small></div>
          </div>
        </section>

        <section class="landing-cinematic-chapter landing-procedure" id="field-guide" data-cinematic-chapter data-chapter="arrival" aria-labelledby="guide-title">
          <div class="landing-cinematic-stage">
            <div class="landing-cinematic-media landing-procedure-media" aria-hidden="true">
              <div class="landing-cinematic-image"></div>
              <div class="landing-procedure-shutter"></div>
            </div>
            <div class="landing-procedure-layout">
              <header class="landing-procedure-heading">
                <span class="landing-micro-label">ARRIVAL PROCEDURE // FOUR CLEARANCES</span>
                <h2 id="guide-title">Find the signal.<br />Leave a record.</h2>
                <p>Four steps take you from the Evrima server list to your own secure field terminal.</p>
              </header>
              <ol class="landing-procedure-steps">
                <li data-procedure-step="0"><span>01</span><div><small>Signal acquisition</small><h3>Find The Nest</h3><p>Search the Evrima server list for <strong>${escapeLandingText(SERVER_LISTING_NAME)}</strong>.</p></div></li>
                <li data-procedure-step="1"><span>02</span><div><small>Field entry</small><h3>Enter Gateway</h3><p>Join, choose your dinosaur, and survive. The game remains the source of truth for your active life.</p></div></li>
                <li data-procedure-step="2"><span>03</span><div><small>Identity handshake</small><h3>Link one profile</h3><p>Use Steam directly, or begin with Discord and complete the one-time <code>!link</code> verification in game.</p></div></li>
                <li data-procedure-step="3"><span>04</span><div><small>Terminal clearance</small><h3>Open your portal</h3><p>Your live dinosaur, garage, Sticks, skins, map, and support tools load against the verified profile.</p></div></li>
              </ol>
              <div class="landing-procedure-progress" aria-hidden="true"><span><i></i></span><small data-procedure-counter>01 / 04</small></div>
              <p class="landing-trust-note"><i aria-hidden="true">◆</i>No passwords or Steam credentials are ever entered into The Nest.</p>
            </div>
          </div>
        </section>

        <section class="landing-rules" id="rules" data-chapter="protocols" aria-labelledby="rules-title">
          <div class="landing-rules-heading" data-landing-reveal>
            <div class="landing-section-index"><span>04</span><small>Protocols</small></div>
            <div><span class="landing-micro-label">PUBLIC COMMUNITY HANDBOOK // OPENING EDITION</span><h2 id="rules-title">The code of survival.</h2></div>
            <p>Rules should be readable before they are enforceable. The full handbook is organised by category below.</p>
          </div>
          <div class="landing-rulebook" data-public-rules>
            ${renderPublicRules(rules)}
          </div>
          <p class="landing-rules-status" data-rules-status role="status" aria-live="polite"></p>
        </section>

        <section class="landing-access" id="access" data-chapter="identity" aria-labelledby="access-title">
          <div class="landing-access-scene" aria-hidden="true">
            <div class="landing-access-image"></div>
            <div class="landing-access-doorlight"></div>
            <div class="landing-access-scan"></div>
          </div>
          <div class="landing-access-layout">
            <div class="landing-section-index"><span>05</span><small>Identity</small></div>
            <div class="landing-access-copy" data-landing-reveal>
              <span class="landing-micro-label">IDENTITY CHAMBER // SECURE CONNECTION</span>
              <h2 id="access-title">The door is open.<br />Identify yourself.</h2>
              <p>Choose the route that matches where you are starting. Both arrive at the same player profile once Steam and Discord are linked.</p>
            </div>
            <div class="landing-access-routes">
              <article class="landing-access-route is-primary" data-landing-reveal>
                <div class="landing-route-head"><span>01</span><small>Recommended</small></div>
                <h3>Continue with Steam</h3>
                <p>The quickest route. Steam verifies your identity and opens the correct game profile directly.</p>
                <a href="${STEAM_LOGIN_PATH}"><span>Enter with Steam</span><i aria-hidden="true">↗</i></a>
              </article>
              <article class="landing-access-route" data-landing-reveal>
                <div class="landing-route-head"><span>02</span><small>Alternative</small></div>
                <h3>Continue with Discord</h3>
                <p>Use Discord first, then attach Steam safely with the one-time in-game verification code.</p>
                <a href="${DISCORD_LOGIN_PATH}"><span>Enter with Discord</span><i aria-hidden="true">↗</i></a>
              </article>
            </div>
            <div class="landing-access-foot" data-landing-reveal>
              <span><i aria-hidden="true"></i>Encrypted identity hand-off</span>
              <a href="#field-guide">Need help linking your account?</a>
            </div>
          </div>
        </section>
      </main>

      <footer class="landing-footer">
        <div class="landing-footer-brand">
          <img src="/portal/assets/the-nest-logo.png" alt="" />
          <div><strong>The Nest</strong><span>Independent Evrima community</span></div>
        </div>
        <div class="landing-footer-links">
          <a href="#about">About</a>
          <a href="#rules">Rules</a>
          <a href="#access">Player portal</a>
          <a href="https://www.patreon.com/TheNestSurvival" target="_blank" rel="noopener noreferrer">Patreon</a>
          <a href="${discordInviteUrl ?? "#access"}" target="_blank" rel="noopener noreferrer" data-landing-discord-invite ${discordInviteUrl ? "" : "hidden"}>Join Discord</a>
        </div>
        <p>The Nest is an independent community server and is not affiliated with Afterthought LLC. The Isle and related marks belong to their respective owners.</p>
      </footer>
    </div>`;
}

export function initializePublicLanding(root: ParentNode = document): void {
  const landing = root.querySelector<HTMLElement>("[data-public-landing]");
  if (!landing || landing.dataset.initialized === "true") return;
  landing.dataset.initialized = "true";

  document.body.classList.add("public-landing-active");
  requestAnimationFrame(() => landing.classList.add("landing-motion-ready"));

  const header = landing.querySelector<HTMLElement>("[data-landing-header]");
  const signalDock = landing.querySelector<HTMLElement>("[data-signal-dock]");
  const cinematicChapters = Array.from(landing.querySelectorAll<HTMLElement>("[data-cinematic-chapter]"));
  const breachChapter = cinematicChapters.find((chapter) => chapter.dataset.chapter === "breach");
  const navigationSections = Array.from(landing.querySelectorAll<HTMLElement>("[data-chapter]"));
  const chapterLinks = Array.from(landing.querySelectorAll<HTMLAnchorElement>("[data-chapter-link]"));
  const motionToggle = landing.querySelector<HTMLButtonElement>("[data-motion-toggle]");
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  let effectsEnabled = !motionQuery.matches;
  let manualMotionPreference = false;
  let motionFrame = 0;
  let pointerFrame = 0;
  let pointerX = 50;
  let pointerY = 50;

  const effectsAreActive = () => effectsEnabled && window.innerWidth > 820;
  const updateEffectsState = () => {
    const active = effectsAreActive();
    landing.classList.toggle("landing-effects-off", !active);
    if (motionToggle) {
      motionToggle.setAttribute("aria-pressed", String(active));
      const label = motionToggle.querySelector<HTMLElement>("span");
      if (label) label.textContent = active ? "Effects on" : "Effects off";
    }
  };

  const updateMotion = () => {
    motionFrame = 0;
    const viewportHeight = Math.max(1, window.innerHeight);
    const cinematicMotionActive = effectsAreActive();
    const chapterReads = cinematicChapters.map((chapter) => {
      const bounds = chapter.getBoundingClientRect();
      const travel = Math.max(1, bounds.height - viewportHeight);
      const progress = cinematicMotionActive ? Math.max(0, Math.min(1, -bounds.top / travel)) : 0;
      return { chapter, progress };
    });
    const navigationReads = navigationSections.map((section) => ({ section, bounds: section.getBoundingClientRect() }));
    const pageTravel = Math.max(1, document.documentElement.scrollHeight - viewportHeight);
    const activeSection = navigationReads
      .filter(({ bounds }) => bounds.top <= viewportHeight * 0.52 && bounds.bottom >= viewportHeight * 0.48)
      .sort((left, right) => Math.abs(left.bounds.top) - Math.abs(right.bounds.top))[0]?.section;

    landing.style.setProperty("--page-progress", (window.scrollY / pageTravel).toFixed(4));
    landing.style.setProperty("--hero-progress", Math.max(0, Math.min(1, window.scrollY / viewportHeight)).toFixed(4));
    header?.classList.toggle("is-scrolled", window.scrollY > 24);
    const dockVisible = window.scrollY > viewportHeight * 0.74 && activeSection?.dataset.chapter !== "identity";
    signalDock?.classList.toggle("is-visible", dockVisible);
    signalDock?.toggleAttribute("inert", !dockVisible);
    signalDock?.setAttribute("aria-hidden", String(!dockVisible));

    for (const { chapter, progress } of chapterReads) {
      const beat = Math.min(3, Math.floor(progress * 4));
      chapter.style.setProperty("--chapter-progress", progress.toFixed(4));
      chapter.dataset.beat = String(beat);
      if (chapter.dataset.chapter === "record") {
        const counter = chapter.querySelector<HTMLElement>("[data-record-counter]");
        if (counter) counter.textContent = String(beat + 1).padStart(2, "0");
      }
      if (chapter.dataset.chapter === "arrival") {
        const counter = chapter.querySelector<HTMLElement>("[data-procedure-counter]");
        if (counter) counter.textContent = `${String(beat + 1).padStart(2, "0")} / 04`;
      }
    }

    const activeChapter = activeSection?.dataset.chapter;
    for (const link of chapterLinks) {
      const isActive = link.dataset.chapterLink === activeChapter;
      link.classList.toggle("is-active", isActive);
      if (isActive) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    }
  };

  const scheduleMotion = () => {
    if (motionFrame) return;
    motionFrame = requestAnimationFrame(updateMotion);
  };

  updateEffectsState();
  updateMotion();
  window.addEventListener("scroll", scheduleMotion, { passive: true });
  window.addEventListener("resize", () => {
    updateEffectsState();
    scheduleMotion();
  }, { passive: true });

  if ("IntersectionObserver" in window) {
    const chapterObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) entry.target.classList.toggle("is-active", entry.isIntersecting);
    }, { rootMargin: "-12% 0px -12%", threshold: 0.01 });
    navigationSections.forEach((chapter) => chapterObserver.observe(chapter));
  }

  const revealTargets = Array.from(landing.querySelectorAll<HTMLElement>("[data-landing-reveal]"));
  if (motionQuery.matches || !("IntersectionObserver" in window)) {
    revealTargets.forEach((target) => target.classList.add("is-visible"));
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (entry.target as HTMLElement).classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -8%", threshold: 0.12 });
    revealTargets.forEach((target) => observer.observe(target));
  }

  landing.querySelectorAll<HTMLAnchorElement>("a[href^='#']").forEach((link) => {
    link.addEventListener("click", () => landing.querySelector<HTMLDetailsElement>(".landing-mobile-nav")?.removeAttribute("open"));
  });

  landing.addEventListener("click", async (event) => {
    if (!(event.target instanceof Element)) return;
    const copyButton = event.target.closest<HTMLButtonElement>("[data-copy-server]");
    if (copyButton) {
      const label = copyButton.querySelector<HTMLElement>("[data-copy-label]");
      try {
        await navigator.clipboard.writeText(copyButton.dataset.serverName ?? SERVER_LISTING_NAME);
        if (label) label.textContent = "Copied — paste into Evrima";
        copyButton.classList.add("is-copied");
        window.setTimeout(() => {
          if (label) label.textContent = "Copy server name";
          copyButton.classList.remove("is-copied");
        }, 2600);
      } catch {
        if (label) label.textContent = "Search The Nest in Evrima";
      }
      return;
    }

    const rulebook = landing.querySelector<HTMLElement>("[data-public-rules]");
    const skunkTerminal = landing.querySelector<HTMLElement>("[data-skunk-terminal]");
    const skunkTrigger = landing.querySelector<HTMLButtonElement>("[data-skunk-trigger]");
    if (event.target.closest("[data-skunk-trigger]")) {
      if (skunkTerminal && skunkTrigger) {
        skunkTerminal.hidden = false;
        skunkTrigger.setAttribute("aria-expanded", "true");
        skunkTerminal.querySelector<HTMLButtonElement>("[data-skunk-close]")?.focus();
      }
      return;
    }
    if (event.target.closest("[data-skunk-close]")) {
      if (skunkTerminal && skunkTrigger) {
        skunkTerminal.hidden = true;
        skunkTrigger.setAttribute("aria-expanded", "false");
        skunkTrigger.focus();
      }
      return;
    }
    const categoryLink = event.target.closest<HTMLAnchorElement>(".landing-rule-tools nav a");
    if (categoryLink?.hash) {
      const category = document.getElementById(decodeURIComponent(categoryLink.hash.slice(1)));
      if (category instanceof HTMLDetailsElement) category.open = true;
    }
    if (event.target.closest("[data-rules-expand]")) {
      rulebook?.querySelectorAll<HTMLDetailsElement>("details").forEach((entry) => { entry.open = true; });
    }
    if (event.target.closest("[data-rules-collapse]")) {
      rulebook?.querySelectorAll<HTMLDetailsElement>("details").forEach((entry) => { entry.open = false; });
    }
  });

  motionToggle?.addEventListener("click", () => {
    manualMotionPreference = true;
    effectsEnabled = !effectsEnabled;
    updateEffectsState();
    scheduleMotion();
  });

  motionQuery.addEventListener("change", (event) => {
    if (!manualMotionPreference) effectsEnabled = !event.matches;
    updateEffectsState();
    scheduleMotion();
  });

  landing.addEventListener("pointermove", (event) => {
    if (!effectsAreActive() || !breachChapter?.classList.contains("is-active")) return;
    pointerX = event.clientX / Math.max(1, window.innerWidth) * 100;
    pointerY = event.clientY / Math.max(1, window.innerHeight) * 100;
    if (pointerFrame) return;
    pointerFrame = requestAnimationFrame(() => {
      pointerFrame = 0;
      landing.style.setProperty("--pointer-x", `${pointerX.toFixed(2)}%`);
      landing.style.setProperty("--pointer-y", `${pointerY.toFixed(2)}%`);
    });
  }, { passive: true });

  landing.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const terminal = landing.querySelector<HTMLElement>("[data-skunk-terminal]");
    const trigger = landing.querySelector<HTMLButtonElement>("[data-skunk-trigger]");
    if (!terminal || terminal.hidden) return;
    terminal.hidden = true;
    trigger?.setAttribute("aria-expanded", "false");
    trigger?.focus();
  });

  if (window.location.hash.length > 1) {
    const initialHash = window.location.hash;
    const scrollToInitialAnchor = () => {
      if (window.location.hash !== initialHash) return;
      let targetId: string;
      try {
        targetId = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }
      const target = document.getElementById(targetId);
      if (!target || !landing.contains(target)) return;
      if (target instanceof HTMLDetailsElement) target.open = true;
      target.closest<HTMLDetailsElement>(".landing-rule-section")?.setAttribute("open", "");
      target.scrollIntoView({ block: "start" });
    };
    window.setTimeout(scrollToInitialAnchor, 0);
    window.setTimeout(scrollToInitialAnchor, 250);
  }

  void hydratePublicLanding(landing);
}

export async function hydratePublicLanding(root: ParentNode = document): Promise<void> {
  try {
    const response = await fetch("/api/public/landing", { headers: { accept: "application/json" } });
    if (!response.ok) return;
    const content = await response.json() as PublicLandingContent;
    const rulebook = root.querySelector<HTMLElement>("[data-public-rules]");
    if (rulebook && Array.isArray(content.rules) && content.rules.length >= PUBLIC_LANDING_RULES.length) {
      rulebook.innerHTML = renderPublicRules(content.rules);
      const rulesStatus = root.querySelector<HTMLElement>("[data-rules-status]");
      if (rulesStatus) rulesStatus.textContent = "The public rulebook has been updated.";
    }
    const invite = root.querySelector<HTMLAnchorElement>("[data-landing-discord-invite]");
    const safeInvite = safeDiscordInvite(content.discordInviteUrl);
    if (invite && safeInvite) {
      invite.href = safeInvite;
      invite.hidden = false;
    }
  } catch {
    // The public page remains complete when optional community content is unavailable.
  }
}

export function renderPublicRules(rules: PublicLandingRule[]): string {
  if (!rules.length) {
    return `<div class="landing-rulebook-empty"><span>ARCHIVE // SYNCHRONISING</span><h3>Public rulebook being prepared.</h3><p>Read the current rules in The Nest Discord before playing. Once reviewed for public display, the same versioned rules used by staff cases will appear here.</p></div>`;
  }

  const grouped = new Map<string, PublicLandingRule[]>();
  for (const rule of rules.slice(0, 48)) {
    const sectionRules = grouped.get(rule.section) ?? [];
    sectionRules.push(rule);
    grouped.set(rule.section, sectionRules);
  }
  const sections = Array.from(grouped.entries());

  return `
    <div class="landing-rule-tools">
      <nav aria-label="Rule categories">
        ${sections.map(([section], index) => `<a href="#rule-section-${ruleAnchor(section)}"><span>${String(index + 1).padStart(2, "0")}</span>${escapeLandingText(section)}</a>`).join("")}
      </nav>
      <div><button type="button" data-rules-expand>Expand all</button><button type="button" data-rules-collapse>Collapse all</button></div>
    </div>
    <div class="landing-rule-sections">
      ${sections.map(([section, sectionRules], sectionIndex) => `
        <details class="landing-rule-section" id="rule-section-${ruleAnchor(section)}" ${sectionIndex === 0 ? "open" : ""}>
          <summary aria-labelledby="rule-heading-${ruleAnchor(section)}"><span>${String(sectionIndex + 1).padStart(2, "0")}</span><div><small>PROTOCOL GROUP</small><h3 id="rule-heading-${ruleAnchor(section)}">${escapeLandingText(section)}</h3></div><i>${String(sectionRules.length).padStart(2, "0")} RULES</i><b aria-hidden="true"></b></summary>
          <div class="landing-rule-list">
            ${sectionRules.map((rule, ruleIndex) => `
              <details class="landing-rule-entry" id="rule-${ruleAnchor(rule.title)}-${sectionIndex}-${ruleIndex}" ${sectionIndex === 0 && ruleIndex === 0 ? "open" : ""}>
                <summary>
                  <span>${escapeLandingText(rule.code)}</span>
                  <div><small>${escapeLandingText(rule.section)} // V${Math.max(1, Math.trunc(rule.version))}</small><strong>${escapeLandingText(rule.title)}</strong></div>
                  <i aria-hidden="true"></i>
                </summary>
                <div class="landing-rule-text">${formatRuleText(rule.text)}</div>
              </details>`).join("")}
          </div>
        </details>`).join("")}
    </div>`;
}

function formatRuleText(value: string): string {
  return escapeLandingText(value).split(/\r?\n/).filter(Boolean).map((line) => {
    if (line.startsWith("Allowed:")) return `<p class="is-allowed"><strong>Allowed</strong>${line.slice("Allowed:".length)}</p>`;
    if (line.startsWith("Not allowed:")) return `<p class="is-not-allowed"><strong>Not allowed</strong>${line.slice("Not allowed:".length)}</p>`;
    return `<p>${line}</p>`;
  }).join("");
}

function ruleAnchor(value: string): string {
  return value.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "protocol";
}

function safeDiscordInvite(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    const supportedHost = url.hostname === "discord.gg" || url.hostname === "discord.com" || url.hostname === "www.discord.com";
    const supportedPath = url.hostname === "discord.gg" || url.pathname.startsWith("/invite/");
    return url.protocol === "https:" && supportedHost && supportedPath ? url.toString() : null;
  } catch {
    return null;
  }
}

function escapeLandingText(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;"
  })[character] ?? character);
}
